import React, { useMemo } from 'react'
import { Paper, PaperContent } from '@e3dc-react/shell/Elements/Paper'
import { Box, Grid } from '@mui/material'
import GraphicBaseCard from 'src/Libs/graph-cards/components/GraphicBaseCard'
import EnergyCard from 'src/Elements/SystemSettings/Solar/EnergyCard'
import { makeStyles } from 'tss-react/mui'
import RotatingFieldCard from './RotatingFieldCard'
import SystemStatusIndicator from '../SystemStatusIndicator'
import { SolarModule, SystemStatusType } from '../SystemSettingsUtils'
import { useLiveSolar } from 'src/Libs/solar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { asFixedNumber } from 'src/Libs/utility'
import { getTachoGraphValues } from 'src/Libs/system-settings/utilities/default'
import { useGraphicBaseCard } from 'src/Libs/graph-cards'
import SystemSettingsBlankView from 'src/Libs/system-settings/component/SystemSettingsBlankView'
import { UnitTypes } from 'src/Libs/types'

const useStyles = makeStyles()(theme => ({
  contentContainer: {
    paddingTop: theme.spacing(2),
  },
}))

interface SolarOverviewProps {
  solarModule: SolarModule
  storageId?: string
  pvIndex?: string
  children?: React.ReactNode
}

interface Props {
  children?: React.ReactNode
}

const GraphicBaseCardWrapper: React.FC<Props> = ({ children }) => {
  return (
    <Grid container direction={'row'} justifyContent={'space-between'} spacing={2} paddingTop={2}>
      {children}
    </Grid>
  )
}

const SolarOverview: React.FC<SolarOverviewProps> = ({ solarModule, storageId, pvIndex }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const solarLiveData = useLiveSolar(storageId, pvIndex)
  const { phaseTagLabel, temperatureTagLabel, trackerTagLabel } = useGraphicBaseCard()

  const getSolarStatus = (): SystemStatusType => {
    if (solarModule?.pviDeviceState.pviDeviceInService) {
      return 'service'
    } else if (solarModule?.pviDeviceState.pviDeviceWorking) {
      return 'running'
    } else if (solarModule?.pviDeviceState.pviDeviceConnected) {
      return 'between'
    } else {
      return 'missing'
    }
  }

  const pviEnergyAll = useMemo<number[]>(() => solarModule.pviEnergyAll.map(value => asFixedNumber(value)), [solarModule.pviEnergyAll])

  const showEnergyCard = useMemo<boolean>(() => Number(pvIndex) === 0, [pvIndex])

  return (
    <Paper title={t('systemSettings.solar.overview.title')} headerChildren={solarModule ? <SystemStatusIndicator status={getSolarStatus()} /> : null}>
      <PaperContent>
        {solarModule ? (
          <Box className={classes.contentContainer}>
            <GraphicBaseCardWrapper>
              <Grid item xs={12} sm={4}>
                <GraphicBaseCard
                  values={getTachoGraphValues(
                    solarModule.pviAcApparentPower.values.length,
                    solarModule.pviAcApparentPower.min,
                    solarLiveData.pviAcApparentPower.values
                  )}
                  tagLabel={phaseTagLabel}
                  min={asFixedNumber(solarModule.pviAcApparentPower.min)}
                  max={asFixedNumber(solarModule.pviAcApparentPower.max)}
                  title={t('systemSettings.solar.overview.apparentPower')}
                  unit={UnitTypes.voltAmpere}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GraphicBaseCard
                  values={getTachoGraphValues(solarModule.pviAcPower.values.length, solarModule.pviAcPower.min, solarLiveData.pviAcPower.values)}
                  tagLabel={phaseTagLabel}
                  min={asFixedNumber(solarModule.pviAcPower.min)}
                  max={asFixedNumber(solarModule.pviAcPower.max)}
                  title={t('systemSettings.solar.overview.activePower')}
                  unit={UnitTypes.watt}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GraphicBaseCard
                  values={getTachoGraphValues(
                    solarModule.pviAcReactivePower.values.length,
                    solarModule.pviAcReactivePower.min,
                    solarLiveData.pviAcReactivePower.values
                  )}
                  tagLabel={phaseTagLabel}
                  min={asFixedNumber(solarModule.pviAcReactivePower.min)}
                  max={asFixedNumber(solarModule.pviAcReactivePower.max)}
                  title={t('systemSettings.solar.overview.reactivePower')}
                  unit={UnitTypes.var}
                />
              </Grid>
            </GraphicBaseCardWrapper>
            <Grid item xs={12} paddingTop={2}>
              {showEnergyCard && <EnergyCard title={t('systemSettings.solar.overview.energyCardTitle')} energyAll={pviEnergyAll} />}
            </Grid>
            <GraphicBaseCardWrapper>
              <Grid item xs={12} sm={4}>
                <GraphicBaseCard
                  tagLabel={temperatureTagLabel}
                  values={getTachoGraphValues(
                    solarModule.pviTemperature.values.length,
                    solarModule.pviTemperature.min,
                    solarLiveData.pviTemperature.values
                  )}
                  min={asFixedNumber(solarModule.pviTemperature.min)}
                  max={asFixedNumber(solarModule.pviTemperature.max)}
                  title={t('systemSettings.solar.overview.temperature')}
                  unit={UnitTypes.celsius}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GraphicBaseCard
                  tagLabel={phaseTagLabel}
                  values={getTachoGraphValues(
                    solarModule.pviAcVoltage.values.length,
                    solarModule.pviAcVoltage.min,
                    solarLiveData.pviAcVoltage.values
                  )}
                  min={asFixedNumber(solarModule.pviAcVoltage.min)}
                  max={asFixedNumber(solarModule.pviAcVoltage.max)}
                  title={t('systemSettings.solar.overview.acVoltage')}
                  unit={UnitTypes.volt}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GraphicBaseCard
                  tagLabel={phaseTagLabel}
                  values={getTachoGraphValues(
                    solarModule.pviAcCurrent.values.length,
                    solarModule.pviAcCurrent.min,
                    solarLiveData.pviAcCurrent.values
                  )}
                  min={asFixedNumber(solarModule.pviAcCurrent.min)}
                  max={solarModule.pviAcCurrent.max}
                  title={t('systemSettings.solar.overview.acCurrent')}
                  unit={UnitTypes.ampere}
                />
              </Grid>
            </GraphicBaseCardWrapper>
            <Grid item xs={12} paddingTop={2}>
              <RotatingFieldCard
                title={t('systemSettings.solar.overview.rotatingFieldTitle')}
                rotationDirection={solarModule.pviRotaryFieldDirection}
              />
            </Grid>
            <GraphicBaseCardWrapper>
              <Grid item xs={12} sm={4}>
                <GraphicBaseCard
                  tagLabel={trackerTagLabel}
                  values={getTachoGraphValues(
                    solarModule.pviDcVoltage.values.length,
                    solarModule.pviDcVoltage.min,
                    solarLiveData.pviDcVoltage.values
                  )}
                  min={asFixedNumber(solarModule.pviDcVoltage.min)}
                  max={asFixedNumber(solarModule.pviDcVoltage.max)}
                  title={t('systemSettings.solar.overview.dcVoltage')}
                  unit={UnitTypes.volt}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GraphicBaseCard
                  tagLabel={trackerTagLabel}
                  values={getTachoGraphValues(
                    solarModule.pviDcCurrent.values.length,
                    solarModule.pviDcCurrent.min,
                    solarLiveData.pviDcCurrent.values
                  )}
                  min={asFixedNumber(solarModule.pviDcCurrent.min)}
                  max={asFixedNumber(solarModule.pviDcCurrent.max)}
                  title={t('systemSettings.solar.overview.dcCurrent')}
                  unit={UnitTypes.ampere}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GraphicBaseCard
                  tagLabel={trackerTagLabel}
                  values={getTachoGraphValues(solarModule.pviDcPower.values.length, solarModule.pviDcPower.min, solarLiveData.pviDcPower.values)}
                  min={asFixedNumber(solarModule.pviDcPower.min)}
                  max={asFixedNumber(solarModule.pviDcPower.max)}
                  title={t('systemSettings.solar.overview.dcPower')}
                  unit={UnitTypes.watt}
                />
              </Grid>
            </GraphicBaseCardWrapper>
          </Box>
        ) : (
          <SystemSettingsBlankView />
        )}
      </PaperContent>
    </Paper>
  )
}

export default SolarOverview
